import React from 'react';
import type { SlaPolicyScope, TicketPriority } from '@troia-v3/types';

import { MultiSelect } from '@/shared/components/ui';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { useCustomersDropdown } from '@/modules/crm/customers/hooks';
import { usePipelinesDropdown } from '@/modules/helpdesk/pipelines/hooks';

import type { SlaPolicyDraft } from '../utils/slaPolicyHelpers';

export interface SlaScopeTabProps {
  draft: SlaPolicyDraft;
  onChange: (next: SlaPolicyDraft) => void;
  /** Aba ativa — gate dos fetches (NUNCA #14). */
  enabled: boolean;
}

const PRIORITY_OPTIONS: Array<{ value: TicketPriority; label: string }> = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

/**
 * Aba "Escopo" — para QUEM a política vale.
 *
 * Campo vazio = "qualquer valor". Uma política sem nenhum campo preenchido
 * casa tudo, o que é exatamente o papel da política padrão (a última da
 * cascata). Quanto mais específico o escopo, mais alto na ordem a política
 * deve ficar — quem decide isso é o arrasto da listagem (C17).
 *
 * `stageIds` NÃO é exposto: o escopo por etapa existe no tipo, mas configurar
 * prazo por etapa de pipeline é a mesma pergunta que a aba "Alertas" do
 * pipeline responde para estagnação, e oferecer os dois lados aqui convida a
 * confundir contrato com estagnação. Fica fora até alguém pedir.
 */
export const SlaScopeTab: React.FC<SlaScopeTabProps> = ({ draft, onChange, enabled }) => {
  const isConversation = draft.entityType === 'conversation';

  const { data: customers } = useCustomersDropdown({ enabled });
  const { data: pipelines } = usePipelinesDropdown({ enabled: enabled && !isConversation });
  const { data: channels } = useChannelsDropdown({ enabled });

  const patchScope = (patch: Partial<SlaPolicyScope>) =>
    onChange({ ...draft, scope: { ...draft.scope, ...patch } });

  const field = (label: string, hint: string, node: React.ReactNode) => (
    <div>
      <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>{label}</label>
      {node}
      <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>{hint}</p>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="sla-policy-scope-tab">
      <p className="text-xs leading-relaxed" style={{ color: '#64748b' }}>
        Deixe vazio para valer em qualquer valor. A política só casa quando <strong>todos</strong>{' '}
        os campos preenchidos baterem.
      </p>

      {field(
        'Clientes',
        'Vazio = qualquer cliente.',
        <MultiSelect
          options={(customers ?? []).map((c) => ({ value: c.id, label: c.name }))}
          selectedValues={draft.scope.customerIds ?? []}
          onChange={(values) => patchScope({ customerIds: values })}
          placeholder="Qualquer cliente"
          data-testid="sla-policy-scope-customers"
        />,
      )}

      {!isConversation && field(
        'Funis de chamado (pipelines)',
        'Vazio = qualquer funil. Indisponível em política de conversa — conversa não tem funil.',
        <MultiSelect
          options={(pipelines ?? []).map((p) => ({ value: p.id, label: p.name }))}
          selectedValues={draft.scope.pipelineIds ?? []}
          onChange={(values) => patchScope({ pipelineIds: values })}
          placeholder="Qualquer funil"
          data-testid="sla-policy-scope-pipelines"
        />,
      )}

      {field(
        'Canais',
        'Vazio = qualquer canal. É o eixo principal em política de conversa.',
        <MultiSelect
          options={(channels ?? []).map((c) => ({ value: c.id, label: c.name }))}
          selectedValues={draft.scope.channelIds ?? []}
          onChange={(values) => patchScope({ channelIds: values })}
          placeholder="Qualquer canal"
          data-testid="sla-policy-scope-channels"
        />,
      )}

      {field(
        'Prioridades',
        'Vazio = qualquer prioridade. Não confundir com a coluna Prioridade da aba Alvos: aqui decide SE a política casa; lá decide QUAL prazo vale.',
        <MultiSelect
          options={PRIORITY_OPTIONS}
          selectedValues={draft.scope.priorities ?? []}
          onChange={(values) => patchScope({ priorities: values as TicketPriority[] })}
          placeholder="Qualquer prioridade"
          data-testid="sla-policy-scope-priorities"
        />,
      )}
    </div>
  );
};

SlaScopeTab.displayName = 'SlaScopeTab';
